import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users, FileText, DollarSign, CalendarDays,
  BarChart3, AlertTriangle, Clock, CheckCircle2,
  ChevronRight, ArrowRight, UserPlus, FilePlus,
  CalendarCheck, QrCode, Bell, UserCheck, UserX,
  CalendarRange, Umbrella, Cake,
} from 'lucide-react';
import {
  Box, Stack, Grid, Card, Typography, Chip,
  LinearProgress, Skeleton, useMediaQuery, useTheme,
  alpha,
} from '@mui/material';

import apiService from '../../services/apiServices';
import globals from '../../services/globals';
import presenceService from '../../services/presenceService';
import { eventsToPresence } from '../presence/LivePresence';
import EmployeeHome from './EmployeeHome';
import { ANNUAL_LEAVE_TYPE_ID } from '../../config/leaveTypes';

// ── Helpers ──────────────────────────────────────────────────────────────────

const getRoleNames = () => {
  const user = globals().getLogedInUser();
  const roles = (user?.data?.roles || user?.roles || [])
    .map(r => (r?.name || r?.role_name || r || '').toString().toLowerCase().trim())
    .filter(Boolean);
  if (!roles.length && user?.data?.user_role)
    roles.push(String(user.data.user_role).toLowerCase().trim());
  return roles;
};

// ── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, count, action }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 2.5, py: 1.75, borderBottom: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {Icon && (
          <Box
            sx={{
              width: 28, height: 28, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'grey.100', color: 'text.secondary',
            }}
          >
            <Icon size={14} strokeWidth={2} />
          </Box>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
        {typeof count === 'number' && count > 0 && (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20, minWidth: 22, fontSize: '0.6875rem', fontWeight: 600,
              bgcolor: 'grey.100', color: 'text.secondary', border: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Stack>
      {action}
    </Stack>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
// 2025 SaaS style: big metric, muted label, soft shadow, hover lift.
// Single accent (indigo) for the icon container; numeric value uses primary text.

function KPICard({ icon: Icon, label, value, sub, to, loading }) {
  const [displayVal, setDisplayVal] = useState(0);

  useEffect(() => {
    if (loading || value == null) return;
    if (value === 0) { setDisplayVal(0); return; }
    let frameId;
    const start = performance.now();
    const step = (now) => {
      const prog = Math.min((now - start) / 700, 1);
      const eased = 1 - Math.pow(1 - prog, 3);
      setDisplayVal(Math.round(value * eased));
      if (prog < 1) frameId = requestAnimationFrame(step);
    };
    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [loading, value]);

  const content = (
    <Card
      sx={{
        p: { xs: 1.75, md: 2 },
        cursor: to ? 'pointer' : 'default',
        height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        gap: 1,
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
        '&:hover': to ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(17,24,39,0.06), 0 2px 6px rgba(17,24,39,0.04)',
          borderColor: 'grey.300',
        } : {},
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box
          sx={{
            width: 30, height: 30, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'primary.soft', color: 'primary.main',
          }}
        >
          <Icon size={15} strokeWidth={2} />
        </Box>
        {to && <ChevronRight size={14} style={{ color: '#6B7280' }} />}
      </Stack>

      {loading ? (
        <Box>
          <Skeleton variant="text" width="60%" height={12} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="40%" height={28} />
        </Box>
      ) : (
        <Box>
          <Typography
            sx={{
              fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary',
              letterSpacing: '-0.005em', mb: 0.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {label}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography
              sx={{
                fontSize: { xs: '1.375rem', md: '1.625rem' },
                fontWeight: 700, color: 'text.primary',
                lineHeight: 1, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums',
              }}
            >
              {displayVal}
            </Typography>
            {sub && (
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontSize: '0.6875rem', fontWeight: 500 }}
              >
                {sub}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Card>
  );

  return to ? (
    <Box component={Link} to={to} sx={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      {content}
    </Box>
  ) : content;
}

// ── Birthday Banner ──────────────────────────────────────────────────────────

function BirthdayBanner({ people, isMyBirthday }) {
  const { t } = useTranslation();
  if (!people.length) return null;
  const names = people.map(p => p.name).filter(Boolean);
  const displayNames =
    names.length <= 2
      ? names.join(t('overview.andJoin'))
      : names.slice(0, 2).join(', ') + ` +${names.length - 2}`;

  return (
    <Card
      sx={{
        p: 2, display: 'flex', alignItems: 'center', gap: 2,
        bgcolor: isMyBirthday ? 'primary.soft' : 'warning.soft',
        borderColor: (theme) => alpha(isMyBirthday ? theme.palette.primary.main : theme.palette.warning.main, 0.18),
      }}
    >
      <Box
        sx={{
          width: 36, height: 36, borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'background.paper',
          color: isMyBirthday ? 'primary.main' : 'warning.dark',
        }}
      >
        <Cake size={18} strokeWidth={2} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isMyBirthday && (
          <Typography variant="overline" sx={{ color: 'primary.dark', fontSize: '0.625rem', display: 'block', lineHeight: 1.2 }}>
            {t('overview.birthdayToday')}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: '0.875rem', fontWeight: 600,
            // `warning.dark` on `warning.soft` measures 3.07:1 — under the 4.5
            // AA floor. `onSoft` is the tone meant for tinted backgrounds (4.84).
            color: isMyBirthday ? 'primary.dark' : 'warning.onSoft',
            letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {isMyBirthday
            ? t('overview.myBirthdayMsg', { name: people.find(p => p.isMe)?.name || '' })
            : t('overview.otherBirthdayMsg', { names: displayNames })}
        </Typography>
      </Box>
      <Chip
        label={`${people.length} ${t('overview.birthdayLabel')}`}
        size="small"
        sx={{
          bgcolor: 'background.paper',
          // 3.19:1 on white at 0.6875rem — below AA. onSoft lands at 5.02.
          color: isMyBirthday ? 'primary.dark' : 'warning.onSoft',
          fontWeight: 600, fontSize: '0.6875rem', height: 22, border: 0,
        }}
      />
    </Card>
  );
}

// ── My Leave Balance Card ────────────────────────────────────────────────────

function MyLeaveCard({ annual, used, pending, loading }) {
  const { t } = useTranslation();
  const remaining = Math.max(0, Math.round((annual - used) * 1000) / 1000);
  const pct = annual > 0 ? Math.round((used / annual) * 100) : 0;
  const tone = remaining > 10 ? 'success' : remaining > 5 ? 'warning' : 'error';

  return (
    <Box component={Link} to="/my-leaves" sx={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <Card sx={{ p: { xs: 1.75, md: 2 }, height: '100%', transition: 'border-color 0.15s ease', '&:hover': { borderColor: 'grey.300' } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 34, height: 34, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: `${tone}.soft`, color: `${tone}.onSoft`,
            }}
          >
            <Umbrella size={16} strokeWidth={2} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', lineHeight: 1.3 }}>
              {t('overview.myLeaveBalance')}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={120} height={14} />
            ) : (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.6875rem', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {used} {t('overview.usedLabel')} · {pending > 0 ? `${pending} ${t('overview.daysPending')} · ` : ''}{annual} {t('overview.totalLabel')}
              </Typography>
            )}
          </Box>

          {loading ? (
            <Skeleton variant="text" width={48} height={32} />
          ) : (
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                sx={{
                  fontSize: '1.5rem', fontWeight: 700, color: `${tone}.onSoft`,
                  lineHeight: 1, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums',
                }}
              >
                {remaining}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.625rem', fontWeight: 500 }}>
                {t('overview.daysLeft')}
              </Typography>
            </Box>
          )}
        </Stack>

        {!loading && (
          <Box sx={{ mt: 1.25 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(pct, 100)}
              color={tone}
              sx={{ height: 6 }}
            />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.625rem' }}>
                0 {t('overview.daysLabel')}
              </Typography>
              <Typography variant="caption" sx={{ color: `${tone}.onSoft`, fontSize: '0.625rem', fontWeight: 600 }}>
                {pct}{t('overview.pctUsed')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.625rem' }}>
                {annual} {t('overview.daysLabel')}
              </Typography>
            </Stack>
          </Box>
        )}
      </Card>
    </Box>
  );
}

// ── Presence bar ─────────────────────────────────────────────────────────────

function PresenceBar({ presentCount, absentCount, total, loading }) {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.round((presentCount / total) * 100) : 0;
  const tone = presentCount > 0 ? 'success' : 'warning';

  return (
    <Box component={Link} to="/presence-live" sx={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <Card sx={{ p: { xs: 1.75, md: 2 }, height: '100%', transition: 'border-color 0.15s ease', '&:hover': { borderColor: 'grey.300' } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 34, height: 34, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: `${tone}.soft`, color: `${tone}.onSoft`,
            }}
          >
            <UserCheck size={16} strokeWidth={2} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', lineHeight: 1.3 }}>
              {t('overview.presenceToday')}
            </Typography>
            {!loading && (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.6875rem', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('overview.present', { count: presentCount })} · {t('overview.absent', { count: absentCount })}
              </Typography>
            )}
          </Box>
          {!loading && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                icon={<UserCheck size={11} strokeWidth={2.5} />}
                label={presentCount}
                size="small"
                color="success"
                sx={{ height: 22, fontVariantNumeric: 'tabular-nums', fontWeight: 700, '& .MuiChip-icon': { ml: 0.5, mr: -0.25 } }}
              />
              <Chip
                icon={<UserX size={11} strokeWidth={2.5} />}
                label={absentCount}
                size="small"
                color="warning"
                sx={{ height: 22, fontVariantNumeric: 'tabular-nums', fontWeight: 700, '& .MuiChip-icon': { ml: 0.5, mr: -0.25 } }}
              />
              {total > 0 && (
                <Chip
                  label={`${pct}%`}
                  size="small"
                  sx={{
                    height: 22, bgcolor: 'grey.100', color: 'text.primary',
                    fontVariantNumeric: 'tabular-nums', fontWeight: 700, border: 0,
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>

        <Box sx={{ mt: 1.25 }}>
          {loading ? (
            <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 999 }} />
          ) : (
            <LinearProgress
              variant="determinate"
              value={pct}
              color={tone}
              sx={{ height: 6 }}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
}

// ── Approved Leaves — compact notification (click to open full list) ─────────

function ApprovedLeavesNotice({ leaves, loading }) {
  const { t } = useTranslation();
  if (loading) return null;

  const now = new Date(); now.setHours(0, 0, 0, 0);
  const active = (leaves || []).filter(h => {
    const end = new Date(h.EndDate || h.end_date || '');
    return !isNaN(end.getTime()) && end >= now;
  });

  if (active.length === 0) return null;

  return (
    <Box
      component={Link}
      to="/inbox"
      sx={{
        textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.5, borderRadius: 2,
        bgcolor: 'background.paper', border: 1, borderColor: 'divider',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': { borderColor: 'grey.300', bgcolor: 'grey.50' },
      }}
    >
      <Box
        sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'primary.soft', color: 'primary.main',
        }}
      >
        <CalendarRange size={15} strokeWidth={2} />
      </Box>
      <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontWeight: 500 }}>
        {t('overview.approvedLeaves')}
      </Typography>
      <Chip
        label={active.length}
        size="small"
        sx={{
          bgcolor: 'primary.soft', color: 'primary.dark',
          fontWeight: 600, height: 22, minWidth: 28, border: 0,
        }}
      />
      <ChevronRight size={14} style={{ color: '#6B7280', flexShrink: 0 }} />
    </Box>
  );
}

// ── Alert / OK rows ──────────────────────────────────────────────────────────

function AlertRow({ icon: Icon, title, detail, to, isLast, severity }) {
  const tone = severity === 'high' ? 'error' : severity === 'mid' ? 'warning' : 'info';
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2.5, py: 1.5,
        borderBottom: isLast ? 0 : 1, borderColor: 'divider',
        transition: 'background-color 0.1s ease',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Box
        sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${tone}.soft`, color: `${tone}.onSoft`,
        }}
      >
        <Icon size={15} strokeWidth={2} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600, color: 'text.primary',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {detail}
        </Typography>
      </Box>
      <ArrowRight size={14} style={{ color: '#6B7280', flexShrink: 0 }} />
    </Box>
  );
}

function OkRow({ label, isLast }) {
  return (
    <Stack
      direction="row" alignItems="center" spacing={1.5}
      sx={{
        px: 2.5, py: 1.25,
        borderBottom: isLast ? 0 : 1, borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'success.soft', color: 'success.onSoft',
        }}
      >
        <CheckCircle2 size={15} strokeWidth={2} />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
        {label}
      </Typography>
      <Chip label="OK" size="small" color="success" sx={{ height: 20, fontWeight: 600 }} />
    </Stack>
  );
}

// ── Action button (quick actions list) ───────────────────────────────────────

function ActionBtn({ icon: Icon, label, to, desc }) {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.5, py: 1.25, mx: 1, borderRadius: 1.5,
        transition: 'background-color 0.1s ease',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Box
        sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'grey.100', color: 'text.secondary',
          transition: 'all 0.15s ease',
          '.MuiBox-root:hover > &': { bgcolor: 'primary.soft', color: 'primary.main' },
        }}
      >
        <Icon size={15} strokeWidth={2} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {label}
        </Typography>
        {desc && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mt: 0.25 }}>
            {desc}
          </Typography>
        )}
      </Box>
      <ArrowRight size={14} style={{ color: '#6B7280' }} />
    </Box>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function Overview() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [expiringContracts, setExpiringContracts] = useState(0);
  const [lowAnnualBalance, setLowAnnualBalance] = useState(0);
  const [startingTomorrow, setStartingTomorrow] = useState(0);
  const [unregisteredATK, setUnregisteredATK] = useState(0);
  const [presentCount, setPresentCount] = useState(0);
  const [absentCount, setAbsentCount] = useState(0);
  const [birthdayPeople, setBirthdayPeople] = useState([]);
  const [myLeaveStats, setMyLeaveStats] = useState({ used: 0, pending: 0, annual: 20 });
  const [approvedLeaves, setApprovedLeaves] = useState([]);

  const countUnregisteredATK = useCallback((list = []) => {
    const getATKDate = w => w?.ATKRegisteredAt || w?.atkRegisteredAt || w?.AtkRegisteredAt || '';
    const isConfirmed = w => {
      const v = w?.ATKRegistered ?? w?.atkRegistered ?? w?.AtkRegistered;
      if (v === true) return true;
      if (v === false) return false;
      if (typeof v === 'number') return v === 1;
      return ['1', 'true', 'po', 'yes'].includes(String(v || '').toLowerCase());
    };
    return list.filter(it => !getATKDate(it) || !isConfirmed(it)).length;
  }, []);

  const roleNames = getRoleNames();
  const hasAdminRole = roleNames.some(r => ['admin', 'menaxher', 'manager'].includes(r));
  const isWorkerOnly = roleNames.length > 0 && !hasAdminRole;

  const _currentUser = globals().getLogedInUser();
  const _myData = _currentUser?.data || _currentUser || {};
  const _myWorkerId = String(_myData.worker_id || _myData.Id || _myData.id || '');

  // Workers get the dedicated EmployeeHome workspace.
  // Admins keep the existing scope-frozen Overview below.
  const renderEmployeeHome = isWorkerOnly;

  useEffect(() => {
    // EmployeeHome owns its own data fetching — skip admin queries for workers.
    if (renderEmployeeHome) { setLoading(false); return; }
    let mounted = true;
    async function load() {
      let annual = 20;
      try {
        const [w, s, c, h] = await Promise.all([
          apiService().get('workers'),
          apiService().get('salariesmonthlylists'),
          apiService().get('workerContract'),
          apiService().get('holidays'),
        ]);
        if (!mounted) return;

        if (w?.success) {
          setWorkers(w.data || []);
          setUnregisteredATK(countUnregisteredATK(w.data || []));
        }

        try {
          const events = await presenceService().getDay();
          const presMap = eventsToPresence(events);
          const checkedInNow = Object.values(presMap).filter(e => e.checkIn && !e.checkOut).length;
          const totalW = (w?.data || []).length;
          setPresentCount(checkedInNow);
          setAbsentCount(Math.max(0, totalW - checkedInNow));
        } catch (_) {}

        if (s?.success) setSalaries(s.data || []);
        if (c?.success) setContracts(c.data || []);
        if (h?.success) {
          setHolidays(h.data || []);
          setPendingLeaves((h.data || []).filter(x => !x.Approve).length);
        }

        if (w?.success) {
          const todayM = new Date().getMonth() + 1;
          const todayD = new Date().getDate();
          const bPeople = (w.data || []).filter(worker => {
            const dob = worker.DateOfBirth || worker.dateOfBirth || '';
            if (!dob) return false;
            const d = new Date(dob);
            if (isNaN(d.getTime())) return false;
            return (d.getMonth() + 1) === todayM && d.getDate() === todayD;
          }).map(worker => ({
            name: `${worker.FirstName || worker.first_name || ''} ${worker.LastName || worker.last_name || ''}`.trim(),
            id: String(worker.Id || worker.id || ''),
            isMe: _myWorkerId ? String(worker.Id || worker.id || '') === _myWorkerId : false,
          }));
          setBirthdayPeople(bPeople);
        }

        if (h?.success) {
          const approved = (h.data || []).filter(lv => lv.Approve && Number(lv.HolidayType) === 1);
          setApprovedLeaves(approved);
        }

        try { const s2 = await globals().getSettings(); if (s2) annual = Number(s2.AnnualLeaveAllowance) || 20; } catch (_) {}

        if (h?.success && _myWorkerId) {
          // Round day sums to 3 decimals — enough precision for minutes/hours
          // converted to days, while cutting float artifacts like 2.5999999999999996.
          const round3 = (n) => Math.round(n * 1000) / 1000;
          const mine = (h.data || []).filter(lv => String(lv.WorkerId) === _myWorkerId);
          const usedDays = mine
            // Vetëm pushimi vjetor e ngarkon balancën vjetore — njësoj si
            // ANNUAL_LEAVE_TYPES te backend/services/leaveSummary.js. Lloji 6
            // këtu ishte "me pagesë"; te `leave_types` ai numër është martesori.
            .filter(lv => Number(lv.HolidayType) === ANNUAL_LEAVE_TYPE_ID && lv.Approve)
            .reduce((sum, lv) => sum + (Number(lv.holidayDays || lv.HolidayDays) || 0), 0);
          const pendingDays = mine
            .filter(lv => !lv.Approve)
            .reduce((sum, lv) => sum + (Number(lv.holidayDays || lv.HolidayDays) || 0), 0);
          setMyLeaveStats({ used: round3(usedDays), pending: round3(pendingDays), annual });
        }

        if (c?.success) {
          const now = new Date(), DAY = 86400000;
          setExpiringContracts((c.data || []).filter(con => {
            const e = new Date(con.EndDate); if (isNaN(e)) return false;
            const d = Math.floor((e - now) / DAY); return d >= 0 && d <= 30;
          }).length);
          const toYMD = d => `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
          const tmw = new Date(); tmw.setDate(tmw.getDate() + 1);
          setStartingTomorrow((c.data || []).filter(con => {
            if (!con.StartDate) return false;
            const sd = new Date(con.StartDate);
            return !isNaN(sd) && toYMD(sd) === toYMD(tmw);
          }).length);
        }

        if (h?.success) {
          const usage = new Map();
          (h.data || []).forEach(it => {
            if (it.HolidayType !== 1) return;
            const k = it.Employee || it.EmployeeName;
            usage.set(k, (usage.get(k) || 0) + (Number(it.holidayDays || 0) || 0));
          });
          setLowAnnualBalance(Array.from(usage.values()).filter(u => (annual - u) < 5).length);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [countUnregisteredATK]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = [
    { icon: Users,        label: t('overview.employees'), value: workers.length,   sub: t('overview.total'),     to: '/workerlist' },
    { icon: FileText,     label: t('overview.contracts'), value: contracts.length, sub: t('overview.active'),    to: '/contractlist' },
    { icon: DollarSign,   label: t('overview.payroll'),   value: salaries.length,  sub: t('overview.thisMonth'), to: '/salarieslist' },
    { icon: CalendarDays, label: t('overview.leaves'),    value: holidays.length,  sub: t('overview.requests'),  to: '/inbox' },
  ];

  const alerts = useMemo(() => [
    unregisteredATK > 0   && { icon: AlertTriangle, severity: 'high', title: t('overview.alertNoAtk'),            detail: t('overview.workers_count',   { count: unregisteredATK }),   to: '/workerlist' },
    startingTomorrow > 0  && { icon: AlertTriangle, severity: 'high', title: t('overview.alertUrgentAtk'),         detail: t('overview.workers_count',   { count: startingTomorrow }),   to: '/workerlist' },
    pendingLeaves > 0     && { icon: Clock,         severity: 'mid',  title: t('overview.alertPendingLeaves'),     detail: t('overview.requests_count',  { count: pendingLeaves }),      to: '/inbox' },
    expiringContracts > 0 && { icon: FileText,      severity: 'low',  title: t('overview.alertExpiringContracts'), detail: t('overview.contracts_count', { count: expiringContracts }),  to: '/contractlist' },
    lowAnnualBalance > 0  && { icon: AlertTriangle, severity: 'mid',  title: t('overview.alertLowBalance'),        detail: `${lowAnnualBalance} ${t('overview.employees_single')}`,     to: '/home/reports/employee-summary' },
  ].filter(Boolean), [unregisteredATK, startingTomorrow, pendingLeaves, expiringContracts, lowAnnualBalance, t]);

  const okItems = [
    unregisteredATK === 0   ? t('overview.okAtk')           : null,
    startingTomorrow === 0  ? t('overview.okStarting')       : null,
    pendingLeaves === 0     ? t('overview.okPendingLeaves')  : null,
    expiringContracts === 0 ? t('overview.okContracts')      : null,
  ].filter(Boolean);

  const actions = [
    // Krijimi i punëtorit ka një formë të vetme (`/workerlist/register`);
    // `/workerlist/addworker` ridrejton aty, por lidhja shkon drejt.
    { icon: UserPlus,      label: t('overview.addEmployee'),   desc: t('overview.addEmployeeDesc'),   to: '/workerlist/register' },
    { icon: FilePlus,      label: t('overview.newContract'),   desc: t('overview.newContractDesc'),   to: '/contractlist/addcontract' },
    { icon: CalendarCheck, label: t('overview.approveLeave'),  desc: t('overview.approveLeaveDesc'),  to: '/inbox' },
    { icon: BarChart3,     label: t('overview.reportsLabel'),  desc: t('overview.reportsDesc'),       to: '/home/reports' },
  ];

  const localeMap = { sq: 'sq-AL', en: 'en-US', de: 'de-DE' };
  const today = new Date().toLocaleDateString(localeMap[i18n.language] || i18n.language, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const iAmBirthday = birthdayPeople.some(p => p.isMe);
  const firstName = _myData.first_name || _myData.FirstName || _myData.firstname || _myData.name || '';

  // Worker-only users get the modern productivity workspace.
  // All hooks above must run unconditionally to keep rules-of-hooks happy.
  if (renderEmployeeHome) return <EmployeeHome />;

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1, minHeight: 0,
          // Inner scroll only kicks in on truly small viewports where content cannot fit
          overflowY: { xs: 'auto', lg: 'hidden' },
          overflowX: 'hidden',
          px: { xs: 2, sm: 2.5, md: 3 },
          py: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1600,
            mx: 'auto',
            height: { xs: 'auto', lg: '100%' },
            // Packed grid: each section is a row; the last row (alerts+actions)
            // takes whatever vertical space remains so nothing overflows on desktop.
            display: { xs: 'flex', lg: 'grid' },
            flexDirection: { xs: 'column', lg: 'initial' },
            gridTemplateRows: { lg: 'auto auto auto auto 1fr' },
            gap: { xs: 1.5, md: 2 },
          }}
        >

          {/* ── Greeting / page title (compact) ── */}
          <Stack
            direction="row"
            alignItems={{ xs: 'flex-start', sm: 'baseline' }}
            justifyContent="space-between"
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
                letterSpacing: '-0.025em',
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              {firstName ? t('overview.helloName', `Hello, ${firstName}`, { name: firstName }) : t('overview.helloFallback', 'Hello')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textTransform: 'capitalize',
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
              }}
            >
              {today}
            </Typography>
          </Stack>

          {/* ── KPI Grid ── */}
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {(isWorkerOnly ? kpis.filter(k => k.to === '/inbox') : kpis).map((k) => (
              <Grid item xs={6} md={3} key={k.to}>
                <KPICard loading={loading} {...k} />
              </Grid>
            ))}
          </Grid>

          {/* ── Birthday Banner (conditional, only renders when needed) ── */}
          {birthdayPeople.length > 0 && (
            <BirthdayBanner people={birthdayPeople} isMyBirthday={iAmBirthday} />
          )}

          {/* ── My Leave + Presence (side by side on md+, packed) ── */}
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            <Grid item xs={12} md={!isWorkerOnly ? 6 : 12}>
              <MyLeaveCard
                annual={myLeaveStats.annual}
                used={myLeaveStats.used}
                pending={myLeaveStats.pending}
                loading={loading}
              />
            </Grid>
            {!isWorkerOnly && (
              <Grid item xs={12} md={6}>
                <PresenceBar
                  presentCount={presentCount}
                  absentCount={absentCount}
                  total={workers.length}
                  loading={loading}
                />
              </Grid>
            )}
          </Grid>

          {/* ── Approved Annual Leaves — compact notice (HR only) ── */}
          {!isWorkerOnly && (
            <ApprovedLeavesNotice leaves={approvedLeaves} loading={loading} />
          )}

          {/* ── Desktop: Alerts + Actions side-by-side (absorbs leftover height) ── */}
          {!isWorkerOnly && isDesktop && (
            <Grid container spacing={2} sx={{ minHeight: 0 }}>
              <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                <Card sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <SectionHeader
                    icon={Bell}
                    title={t('overview.alertsTitle')}
                    count={loading ? null : alerts.length}
                    action={
                      !loading && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {alerts.length === 0
                            ? t('overview.noProblems')
                            : t('overview.inOrder', { count: okItems.length })}
                        </Typography>
                      )
                    }
                  />

                  <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {loading ? (
                      <Stack spacing={1.5} sx={{ p: 2 }}>
                        {[1, 2, 3, 4].map(i => (
                          <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
                            <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1.5 }} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="60%" height={14} />
                              <Skeleton variant="text" width="35%" height={11} />
                            </Box>
                          </Stack>
                        ))}
                      </Stack>
                    ) : alerts.length === 0 && okItems.length === 0 ? (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <CheckCircle2 size={28} style={{ color: '#059669', marginBottom: 6 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('overview.noAlerts')}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                          {t('overview.allSystemsOk')}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {alerts.map((a, i) => (
                          <AlertRow
                            key={i}
                            {...a}
                            isLast={i === alerts.length - 1 && okItems.length === 0}
                          />
                        ))}
                        {okItems.map((label, i) => (
                          <OkRow key={label} label={label} isLast={i === okItems.length - 1} />
                        ))}
                      </>
                    )}
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <Card sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <SectionHeader icon={CalendarCheck} title={t('overview.quickActions')} />
                  <Box sx={{ py: 0.75, flex: 1, overflowY: 'auto' }}>
                    {actions.map(a => <ActionBtn key={a.label} {...a} />)}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* ── Mobile: HR ── */}
          {!isWorkerOnly && !isDesktop && (
            <Stack spacing={2}>
              {/* Alerts */}
              {!loading && alerts.length > 0 && (
                <Card sx={{ overflow: 'hidden' }}>
                  <SectionHeader icon={Bell} title={t('overview.alertsTitle')} count={alerts.length} />
                  {alerts.slice(0, 4).map((a, i) => (
                    <AlertRow key={i} {...a} isLast={i === Math.min(alerts.length, 4) - 1} />
                  ))}
                  {alerts.length > 4 && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', py: 1 }}
                    >
                      {t('overview.moreAlerts', { count: alerts.length - 4 })}
                    </Typography>
                  )}
                </Card>
              )}

              {/* Action pills */}
              <Grid container spacing={1}>
                {actions.map(a => (
                  <Grid item xs={6} sm={3} key={a.label}>
                    <Box
                      component={Link}
                      to={a.to}
                      sx={{
                        textDecoration: 'none', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 1,
                        p: 2, borderRadius: 2.5,
                        bgcolor: 'background.paper',
                        border: 1, borderColor: 'divider',
                        transition: 'border-color 0.15s ease, transform 0.15s ease',
                        '&:hover': { borderColor: 'grey.300', transform: 'translateY(-1px)' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: 2,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'primary.soft', color: 'primary.main',
                        }}
                      >
                        <a.icon size={16} strokeWidth={2} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'center', lineHeight: 1.3 }}
                      >
                        {a.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          )}

          {/* ── Worker-only mobile section ── */}
          {isWorkerOnly && !isDesktop && (
            <Stack spacing={2}>
              <Box
                component={Link}
                to="/scan"
                sx={{
                  textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 2, borderRadius: 2.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                  }}
                >
                  <QrCode size={18} color="#fff" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'inherit' }}>
                    {t('overview.scanPresence')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
                    {t('overview.scanDesc')}
                  </Typography>
                </Box>
                <ChevronRight size={16} color="rgba(255,255,255,0.7)" />
              </Box>

              {alerts.length > 0 && (
                <Card sx={{ overflow: 'hidden' }}>
                  <SectionHeader icon={Bell} title={t('overview.alertsTitle')} count={alerts.length} />
                  {alerts.map((a, i) => <AlertRow key={i} {...a} isLast={i === alerts.length - 1} />)}
                </Card>
              )}
            </Stack>
          )}

          {/* ── Worker-only desktop alerts ── */}
          {isWorkerOnly && isDesktop && alerts.length > 0 && (
            <Card sx={{ overflow: 'hidden' }}>
              <SectionHeader icon={Bell} title={t('overview.alertsTitle')} count={alerts.length} />
              {alerts.map((a, i) => <AlertRow key={i} {...a} isLast={i === alerts.length - 1} />)}
            </Card>
          )}

        </Box>
      </Box>
    </Box>
  );
}
